import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { logout } from "../actions/authenActions";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export const fetchSkipSummary = createAsyncThunk(
  "skipAnalysis/fetchSummary",
  async (
    { start_date, end_date, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/skip_analysis_by_range`,
        { start_date, end_date, liverun, license_key },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchSkipFilings = createAsyncThunk(
  "skipAnalysis/fetchFilings",
  async (
    {
      start_date,
      end_date,
      category_names,
      subtest_names,
      skip_type,
      liverun,
      license_key,
    },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/blood_filings_by_skip`,
        {
          start_date,
          end_date,
          category_names,
          subtest_names,
          skip_type,
          liverun,
          license_key,
        },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchFiling = createAsyncThunk(
  "skipAnalysis/fetchFiling",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/fetch_blood_filing`,
        { lab_report_id, liverun, license_key },
      );
      return res.data || {};
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

const skipAnalysisSlice = createSlice({
  name: "skipAnalysis",
  initialState: {
    liverun: true,
    summary: [],
    filings: [],
    lab: {},
    loadingSummary: false,
    loadingFilings: false,
    loadingLabReport: false,
  },
  reducers: {
    toggleLiverun(state) {
      state.liverun = !state.liverun;
      state.summary = [];
      state.filings = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSkipSummary.pending, (state) => {
        state.loadingSummary = true;
      })
      .addCase(fetchSkipSummary.fulfilled, (state, action) => {
        state.summary = action.payload.map((row, idx) => ({
          id: idx,
          category_names: row.category_names,
          subtest_names: row.subtest_names,
          skip_type: row.skip_type,
          count: row.count,
        }));
        state.loadingSummary = false;
      })

      .addCase(fetchSkipFilings.pending, (state) => {
        state.loadingFilings = true;
      })
      .addCase(fetchSkipFilings.fulfilled, (state, action) => {
        state.filings = action.payload.map((rec, idx) => ({
          id: idx,
          nhs_number: rec[0],
          lab_report_id: rec[1],
          category: rec[2],
          category_type: rec[3],
          status: rec[4],
          starttime: rec[5],
          notes: rec[6],
          msg: rec[7],
        }));
        state.loadingFilings = false;
      })

      .addCase(fetchFiling.pending, (state) => {
        state.loadingLabReport = true;
      })
      .addCase(fetchFiling.fulfilled, (state, action) => {
        const report = action.payload;

        state.lab = {
          license_email: report.license_email || "",
          approved: report.approved ?? null,
          updated_at: report.updated_at || "",
          updated_by: report.updated_by || "",
          patient_data: report.patient_data || {},
          patient_profile: report.patient_profile || {},
          categories: Array.isArray(report.categories) ? report.categories : [],
          actions: Array.isArray(report.actions) ? report.actions : [],
        };

        state.loadingLabReport = false;
      });
  },
});

export const { toggleLiverun } = skipAnalysisSlice.actions;
export default skipAnalysisSlice.reducer;
